import { createRouter, createWebHistory } from 'vue-router'

// view Components
import Home from '@/views/Home.vue'
import Login from '@/views/Auth/Login.vue'

// Operation components
import OrderPdfView from '@/views/Operations/Orders/OrderPdfView.vue'
import ZonePlanning from '@/views/Operations/Planning/ZonePlanning.vue'
import Suggestions from '@/views/Operations/Suggestions/Suggestions.vue'

// Admin components
import Suppliers from '@/views/Administration/Suppliers/Suppliers.vue'
import Inventory from '@/views/Administration/InventoryView.vue'
import SupplierView from '@/views/Administration/Suppliers/SupplierView.vue'
import LocationView from '@/views/Administration/Suppliers/LocationView.vue'
import OdooContacts from '@/views/Administration/Suppliers/OdooContacts.vue'
import Trucks from '@/views/Administration/Trucks.vue'
import Drivers from '@/views/Administration/Drivers.vue'
import CodeGeneratorVue from '@/views/Administration/CodeGenerator.vue'

// Setting components
import Sites from '@/views/Settings/Sites.vue'
import Containers from '@/views/Settings/Containers.vue'
import Users from '@/views/Settings/Users/Users.vue'
import UserView from '@/views/Settings/Users/UserView.vue'
import Zones from '@/views/Settings/Zones/Zones.vue'
import ZoneView from '@/views/Settings/Zones/ZoneView.vue'
const Notifications = () => import('@/views/Settings/Notifications.vue')

// Auth Components
import Signup from '@/views/Auth/Signup.vue'
import NotFound from '@/views/NotFound.vue'

// Heavy desktop views (map/table/datepicker) — lazy so they leave the shared chunk
const Orders = () => import('@/views/Operations/Orders/Orders.vue')
const OrderView = () => import('@/views/Operations/Orders/OrderView.vue')
const Planning = () => import('@/views/Operations/Planning/Planning.vue')
const RouteView = () => import('@/views/Operations/Planning/RouteView.vue')
const Route4Me = () => import('@/views/Administration/Route4Me.vue')
const AnalyticsDashboard = () => import('@/views/Analytics/AnalyticsDashboard.vue')

// Mobile experience — lazy layout + page views (all share meta.layout 'mobile')
const MobileLayout = () => import('@/mobile/MobileLayout.vue')
const MobileOrders = () => import('@/mobile/views/MobileOrders.vue')
const MobileCreateOrder = () => import('@/mobile/views/MobileCreateOrder.vue')
const MobileScheduleRoute = () => import('@/mobile/views/MobileScheduleRoute.vue')
const MobileOrderDetail = () => import('@/mobile/views/MobileOrderDetail.vue')
const MobileRoutes = () => import('@/mobile/views/MobileRoutes.vue')
const MobileRouteDetail = () => import('@/mobile/views/MobileRouteDetail.vue')
const MobileQrGenerator = () => import('@/mobile/views/MobileQrGenerator.vue')
const MobileMore = () => import('@/mobile/views/MobileMore.vue')

import store from '@/store/index'
import Reports from '@/views/Administration/Reports.vue'

// HomeBase Components
import HomeBaseCollectionReceipts from '@/views/Operations/HomeBase/HomeBaseCollectionReceipts.vue'
import HomeBaseCollectionRequests from '@/views/Operations/HomeBase/HomeBaseCollectionRequests.vue'
import HomeBaseSignups from '@/views/Operations/HomeBase/HomeBaseSignups.vue'
import HomeBaseDeployments from '@/views/Operations/HomeBase/HomeBaseDeployments.vue'
import HomeBaseHouseHolds from '@/views/Operations/HomeBase/HomeBaseHouseHolds.vue'
import HomeBaseContainers from '@/views/Operations/HomeBase/HomeBaseContainers.vue'
import HomeBaseUsers from '@/views/Operations/HomeBase/HomeBaseUsers.vue'

// Analytics Components
import AnalyticsSupplierView from '@/views/Analytics/AnalyticsSupplierView.vue'
import SupplierGroups from '@/views/Analytics/SupplierGroups.vue'
import SupplierGroupAnalytics from '@/views/Analytics/SupplierGroupAnalytics.vue'
import AnalyticsReports from '@/views/Analytics/AnalyticsReports.vue'



const requireAuth = (to, from, next) => {
  const user = store.state.user
  const authIsReady = store.state.authIsReady

  // Wait for auth to be ready before making decisions
  if (!authIsReady) {
    // Auth not ready yet, wait for it
    const unsubscribe = store.subscribe((mutation) => {
      if (mutation.type === 'setAuthIsReady' && mutation.payload === true) {
        unsubscribe()
        // Now check authentication
        if (store.state.user) {
          next()
        } else {
          next({ name: 'Login', query: { redirect: to.fullPath } })
        }
      }
    })
  } else {
    // Auth is ready, check user
    if (user) {
      next()
    } else {
      next({ name: 'Login', query: { redirect: to.fullPath } })
    }
  }
}

const requireNoAuth = (to, from, next) => {
  const user = store.state.user
  const authIsReady = store.state.authIsReady

  // Wait for auth to be ready before making decisions
  if (!authIsReady) {
    const unsubscribe = store.subscribe((mutation) => {
      if (mutation.type === 'setAuthIsReady' && mutation.payload === true) {
        unsubscribe()
        // Now check authentication
        if (store.state.user) {
          next({ name: 'Orders' })
        } else {
          next()
        }
      }
    })
  } else {
    // Auth is ready, check user
    if (user) {
      next({ name: 'Orders' })
    } else {
      next()
    }
  }
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    beforeEnter: requireAuth
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    beforeEnter: requireNoAuth,
  },
  {
    path: '/signup',
    name: 'Signup',
    component: Signup,
    beforeEnter: requireNoAuth
  },
  {
    path: '/dashboard/operations/orders',
    name: 'Orders',
    component: Orders,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/suggestions',
    name: 'Suggestions',
    component: Suggestions,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/order-pdf-view',
    name: 'OrderPdfView',
    component: OrderPdfView,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/order-view/:id/:supplierId',
    name: 'OrderView',
    props: true,
    component: OrderView,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/planning',
    name: 'Planning',
    component: Planning,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/zone-planning',
    name: 'ZonePlanning',
    component: ZonePlanning,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/route-view/:id',
    name: 'RouteView',
    props: true,
    component: RouteView,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/suppliers',
    name: 'Suppliers',
    component: Suppliers,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/reports',
    name: 'Reports',
    component: Reports,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/supplier-view/:id',
    name: 'SupplierView',
    props: true,
    component: SupplierView,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/location-view/:supplierId/:locationId',
    name: 'LocationView',
    props: true,
    component: LocationView,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/odoo-contacts',
    name: 'OdooContacts',
    component: OdooContacts,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/trucks',
    name: 'Trucks',
    component: Trucks,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/drivers',
    name: 'Drivers',
    component: Drivers,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/inventory',
    name: 'Inventory',
    component: Inventory,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/route4me',
    name: 'Route4Me',
    component: Route4Me,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/administration/code-generator',
    name: 'CodeGenerator',
    component: CodeGeneratorVue,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/settings/sites',
    name: 'Sites',
    component: Sites,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/settings/containers',
    name: 'Containers',
    component: Containers,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/settings/users',
    name: 'Users',
    component: Users,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/settings/zones',
    name: 'Zones',
    component: Zones,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/settings/zone-view/:id',
    name: 'ZoneView',
    props: true,
    component: ZoneView,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/settings/notifications',
    name: 'Notifications',
    component: Notifications,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/settings/user-view/:id',
    name: 'UserView',
    props: true,
    component: UserView,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/homebase/collection-receipts',
    name: 'HomeBaseCollectionReceipts',
    component: HomeBaseCollectionReceipts,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/homebase/collection-requests',
    name: 'HomeBaseCollectionRequests',
    component: HomeBaseCollectionRequests,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/homebase/signups',
    name: 'HomeBaseSignups',
    component: HomeBaseSignups,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/homebase/deployments',
    name: 'HomeBaseDeployments',
    component: HomeBaseDeployments,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/homebase/households',
    name: 'HomeBaseHouseHolds',
    component: HomeBaseHouseHolds,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/homebase/containers',
    name: 'HomeBaseContainers',
    component: HomeBaseContainers,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/operations/homebase/users',
    name: 'HomeBaseUsers',
    component: HomeBaseUsers,
    beforeEnter: requireAuth,
  },
  // {
  //   path: '/my-account',
  //   name: 'MyAccount',
  //   component: MyAccount,
  //   beforeEnter: requireAuth,
  // }

  {
    path: '/dashboard/analytics',
    name: 'AnalyticsDashboard',
    component: AnalyticsDashboard,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/analytics/supplier/:id',
    name: 'AnalyticsSupplierView',
    props: true,
    component: AnalyticsSupplierView,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/analytics/groups',
    name: 'SupplierGroups',
    component: SupplierGroups,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/analytics/groups/:id',
    name: 'SupplierGroupAnalytics',
    props: true,
    component: SupplierGroupAnalytics,
    beforeEnter: requireAuth,
  },
  {
    path: '/dashboard/analytics/reports',
    name: 'AnalyticsReports',
    component: AnalyticsReports,
    beforeEnter: requireAuth,
  },

  // Mobile experience — bare shell (App.vue branches on meta.layout === 'mobile')
  {
    path: '/mobile',
    component: MobileLayout,
    meta: { layout: 'mobile' },
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: '/mobile/orders',
      },
      {
        path: 'orders',
        name: 'MobileOrders',
        component: MobileOrders,
        meta: { layout: 'mobile' },
      },
      {
        path: 'orders/create',
        name: 'MobileCreateOrder',
        component: MobileCreateOrder,
        meta: { layout: 'mobile' },
      },
      {
        path: 'orders/schedule',
        name: 'MobileScheduleRoute',
        component: MobileScheduleRoute,
        meta: { layout: 'mobile' },
      },
      {
        path: 'orders/:id',
        name: 'MobileOrderDetail',
        component: MobileOrderDetail,
        props: true,
        meta: { layout: 'mobile' },
      },
      {
        path: 'routes',
        name: 'MobileRoutes',
        component: MobileRoutes,
        meta: { layout: 'mobile' },
      },
      {
        path: 'routes/:id',
        name: 'MobileRouteDetail',
        component: MobileRouteDetail,
        props: true,
        meta: { layout: 'mobile' },
      },
      {
        path: 'qr',
        name: 'MobileQrGenerator',
        component: MobileQrGenerator,
        meta: { layout: 'mobile' },
      },
      {
        path: 'more',
        name: 'MobileMore',
        component: MobileMore,
        meta: { layout: 'mobile' },
      },
    ],
  },

  // Catch all 404 - requires auth to prevent unauthorized access
  {
    path: '/:catchAll(.*)',
    name: 'notFound',
    component: NotFound,
    beforeEnter: requireAuth
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Global navigation guard for additional security
router.beforeEach((to, from, next) => {
  const publicPages = ['Login', 'Signup']
  const authRequired = !publicPages.includes(to.name)
  const user = store.state.user
  const authIsReady = store.state.authIsReady

  // If auth is required and not ready, wait for auth state
  if (authRequired && !authIsReady) {
    const unsubscribe = store.subscribe((mutation) => {
      if (mutation.type === 'setAuthIsReady' && mutation.payload === true) {
        unsubscribe()
        if (!store.state.user) {
          next({ name: 'Login', query: { redirect: to.fullPath } })
        } else {
          next()
        }
      }
    })
  } else if (authRequired && authIsReady && !user) {
    // Auth is ready, route requires auth, but no user
    next({ name: 'Login', query: { redirect: to.fullPath } })
  } else {
    // Either public page or user is authenticated
    next()
  }
})

export default router
